import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminOrderRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { IPurchaserResponse } from '../../models/purchasers/purchaser.model';
import { PurchaserApiService } from '../../services/purchaser-api.service';
import {
  CreatePurchaser,
  DeletePurchaser,
  LoadAllPurchasers,
  LoadPurchaserById,
  LoadPurchasers,
  UpdatePurchaser,
  ClearCurrentPurchaser,
} from './purchaser.actions';

export interface PurchaserStateModel {
  purchasersLoaded: boolean;
  purchasers: IPurchaserResponse[];
  allPurchasers: IPurchaserResponse[];
  purchasersTotalPages: number;
  currentPurchaser: IPurchaserResponse;
}

const PURCHASER_STATE_TOKEN = new StateToken<PurchaserStateModel>('purchasers');

@State<PurchaserStateModel>({
  name: PURCHASER_STATE_TOKEN,
  defaults: {
    purchasersLoaded: false,
    purchasers: [],
    allPurchasers: [],
    purchasersTotalPages: 0,
    currentPurchaser: <IPurchaserResponse>{},
  },
})
@Injectable()
export class PurchaserState {
  constructor(private service: PurchaserApiService) {}

  @Action(LoadPurchasers, { cancelUncompleted: true })
  loadPurchasers(
    { getState, setState }: StateContext<PurchaserStateModel>,
    { filter }: LoadPurchasers
  ) {
    return this.service.getPagedPurchasers(filter).pipe(
      tap((response: ApiResponse<IPurchaserResponse[]>) => {
        const state = getState();
        setState({
          ...state,
          purchasersLoaded: true,
          purchasers: response.data,
          purchasersTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllPurchasers, { cancelUncompleted: true })
  loadAllPurchasers(ctx: StateContext<PurchaserStateModel>, { departmentId }: LoadAllPurchasers) {
    return this.service.getAllPurchasers(departmentId).pipe(
      tap((allPurchasers: IPurchaserResponse[]) => {
        ctx.setState(patch({ allPurchasers, purchasers: allPurchasers }));
      })
    );
  }

  @Action(LoadPurchaserById, { cancelUncompleted: true })
  loadPurchaserById(ctx: StateContext<PurchaserStateModel>, { id }: LoadPurchaserById) {
    return this.service.getPurchaserById(id).pipe(
      tap((response: IPurchaserResponse) => {
        ctx.setState(
          patch({
            purchasers: updateItem<IPurchaserResponse>(
              (toUpdate: IPurchaserResponse | undefined) => toUpdate?.id === response.id,
              response
            ),
            currentPurchaser: response,
          })
        );
      })
    );
  }

  @Action(CreatePurchaser, { cancelUncompleted: true })
  @OnSuccess({ url: AdminOrderRoutes.Purchasers, message: 'purchaserCreated' })
  createPurchaser(ctx: StateContext<PurchaserStateModel>, { purchaser }: CreatePurchaser) {
    return this.service.createPurchaser(purchaser);
  }

  @Action(UpdatePurchaser, { cancelUncompleted: true })
  @OnSuccess({ url: AdminOrderRoutes.Purchasers, message: 'purchaserUpdated' })
  updatePurchaser(ctx: StateContext<PurchaserStateModel>, { purchaser }: UpdatePurchaser) {
    return this.service.updatePurchaser(purchaser);
  }

  @Action(DeletePurchaser, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminOrderRoutes.Purchasers,
    message: 'purchaserDeleted',
    dispatch: ResetCurrentFilter,
  })
  deletePurchaser(ctx: StateContext<PurchaserStateModel>, { purchaserId }: DeletePurchaser) {
    return this.service.deletePurchaser(purchaserId);
  }

  @Action(ClearCurrentPurchaser)
  clearCurrentPurchaser(ctx: StateContext<PurchaserStateModel>) {
    ctx.setState(patch({ currentPurchaser: <IPurchaserResponse>{} }));
  }

  @Selector()
  static purchasersLoaded(state: PurchaserStateModel) {
    return state.purchasersLoaded;
  }

  @Selector()
  static getPurchasersTotalPages(state: PurchaserStateModel) {
    return state.purchasersTotalPages;
  }

  @Selector()
  static getPagedPurchasers(state: PurchaserStateModel) {
    return state.purchasers;
  }

  @Selector()
  static getAllPurchasers(state: PurchaserStateModel) {
    return state.allPurchasers;
  }

  @Selector()
  static getCurrentPurchaser(state: any) {
    return state.currentPurchaser;
  }

  @Selector()
  static getPurchaserById(state: any) {
    return (id: string) => {
      return state.purchasers?.find((purchaser: IPurchaserResponse) => purchaser.id === id);
    };
  }
}
